import {
    useContext,
    useState
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    Image,
    View
} from "react-native";
import type OrganizationFormModalProps from "./type";
import type {
    Organization
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitModal,
    NCoreUIKitTheme,
    TextInput,
    CheckBox,
    Button,
    Text
} from "ncore-ui-kit";
import {
    launchImageLibrary
} from "react-native-image-picker";
import {
    BuildingIcon,
    PencilIcon
} from "lucide-react-native";

const ALL_FEATURES = [
    "users",
    "roles",
    "finances",
    "members",
    "courses",
    "students",
    "teachers",
    "finance-categories",
    "charityBoxes"
];
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../../../../../contexts/AuthContext";

const OrganizationFormModal = ({
    initialData,
    onSuccess,
    modalId
}: OrganizationFormModalProps) => {
    const isEdit = !!initialData;
    const {
        changeActiveOrganization,
        activeOrganizationId
    } = useContext(AuthContext);
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        formData,
        setFormData
    ] = useState<Partial<Organization>>({
        authorizedPersonFullName: initialData?.authorizedPersonFullName || "",
        authorizedPersonEmail: initialData?.authorizedPersonEmail || "",
        authorizedPersonPhone: initialData?.authorizedPersonPhone || "",
        profilePicturePath: initialData?.profilePicturePath || "",
        features: initialData?.features || ALL_FEATURES,
        address: initialData?.address || "",
        email: initialData?.email || "",
        phone: initialData?.phone || "",
        name: initialData?.name || ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const handleChange = (key: keyof Organization, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleToggleFeature = (feature: string) => {
        setFormData(prev => {
            const current = prev.features || [];
            if (current.includes(feature)) {
                return {
                    ...prev,
                    features: current.filter(f => f !== feature)
                };
            }
            return {
                ...prev,
                features: [
                    ...current,
                    feature
                ]
            };
        });
    };

    const handleImagePick = async () => {
        const result = await launchImageLibrary({
            includeBase64: true,
            mediaType: "photo",
            quality: 0.5
        });

        if (!result.didCancel && result.assets && result.assets.length > 0) {
            const asset = result.assets[0];
            if (asset) {
                const base64Image = `data:${asset.type || "image/jpeg"};base64,${asset.base64}`;
                handleChange("profilePicturePath", base64Image);
            }
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || formData.name.length < 2) {
            NCoreUIKitToast.open({
                title: localize("org-form-alert-name-length")
            });
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = isEdit
                ? `${process.env.API_URL}/organizations/${initialData.id}`
                : `${process.env.API_URL}/organizations/create`;

            const method = isEdit ? "PUT" : "POST";

            const response = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(formData),
                method
            });

            const data = await response.json();
            if (data.status === "success") {
                NCoreUIKitToast.open({
                    title: data.message || localize("org-form-toast-success")
                });
                onSuccess();
                if (isEdit && activeOrganizationId === initialData?.id) {
                    changeActiveOrganization(activeOrganizationId, formData.features || []);
                }
                NCoreUIKitModal.close({
                    id: modalId
                });
            } else {
                NCoreUIKitToast.open({
                    title: data.message || localize("org-form-toast-error")
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("org-form-toast-server-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <View
        style={[
            stylesheet.container,
            {
                backgroundColor: colors.content.container.default,
                width: isMobile ? "100%" : 500,
                borderRadius: radiuses.md,
                padding: spaces.spacingXl
            }
        ]}
    >
        <Text
            style={stylesheet.unselectableText}
            variant="titleMediumSize"
        >
            {isEdit ? localize("org-form-title-edit") : localize("org-form-title-create")}
        </Text>
        <ScrollView
            contentContainerStyle={[
                stylesheet.formContainer,
                {
                    paddingTop: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
            showsVerticalScrollIndicator={false}
            style={{
                flexShrink: 1
            }}
        >
            <View
                style={[
                    stylesheet.imagePickerContainer,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <TouchableOpacity
                    style={stylesheet.imagePickerTouchable}
                    onPress={handleImagePick}
                    activeOpacity={0.8}
                >
                    {
                        formData.profilePicturePath ?
                            <Image
                                source={{
                                    uri: formData.profilePicturePath
                                }}
                                style={[
                                    stylesheet.profileImage,
                                    {
                                        borderRadius: radiuses.full
                                    }
                                ]}
                            />
                        :
                            <View
                                style={[
                                    stylesheet.profileImage,
                                    {
                                        backgroundColor: colors.content.container.mid,
                                        borderRadius: radiuses.full,
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }
                                ]}
                            >
                                <BuildingIcon
                                    color={colors.content.icon.high}
                                    size={40}
                                />
                            </View>
                    }
                    <View
                        style={[
                            stylesheet.editIconContainer,
                            {
                                backgroundColor: colors.content.container.primary,
                                borderRadius: radiuses.full
                            }
                        ]}
                    >
                        <PencilIcon
                            color={colors.content.icon.high}
                            size={14}
                        />
                    </View>
                </TouchableOpacity>
            </View>
            <TextInput
                placeholder={localize("org-form-name-placeholder")}
                onChangeText={(text) => handleChange("name", text)}
                title={localize("org-form-name-title")}
                defaultValue={formData.name}
                spreadBehaviour="stretch"
            />
            <TextInput
                placeholder={localize("org-form-address-placeholder")}
                onChangeText={(text) => handleChange("address", text)}
                title={localize("org-form-address-title")}
                defaultValue={formData.address}
                spreadBehaviour="stretch"
            />
            <TextInput
                onChangeText={(text) => handleChange("authorizedPersonFullName", text)}
                placeholder={localize("org-form-auth-name-placeholder")}
                defaultValue={formData.authorizedPersonFullName}
                title={localize("org-form-auth-name-title")}
                spreadBehaviour="stretch"
            />
            <TextInput
                onChangeText={(text) => handleChange("authorizedPersonPhone", text)}
                placeholder={localize("org-form-auth-phone-placeholder")}
                title={localize("org-form-auth-phone-title")}
                defaultValue={formData.authorizedPersonPhone}
                spreadBehaviour="stretch"
            />
            <TextInput
                onChangeText={(text) => handleChange("authorizedPersonEmail", text)}
                placeholder={localize("org-form-auth-email-placeholder")}
                defaultValue={formData.authorizedPersonEmail}
                title={localize("org-form-auth-email-title")}
                keyboardType="email-address"
                spreadBehaviour="stretch"
                autoCapitalize="none"
            />
            <TextInput
                placeholder={localize("org-form-phone-placeholder")}
                onChangeText={(text) => handleChange("phone", text)}
                title={localize("org-form-phone-title")}
                defaultValue={formData.phone}
                spreadBehaviour="stretch"
            />
            <TextInput
                placeholder={localize("org-form-email-placeholder")}
                onChangeText={(text) => handleChange("email", text)}
                title={localize("org-form-email-title")}
                defaultValue={formData.email}
                keyboardType="email-address"
                spreadBehaviour="stretch"
                autoCapitalize="none"
            />
            <Text
                style={{
                    marginTop: spaces.spacingSm
                }}
                variant="titleMediumSize"
            >
                {localize("org-form-features-title")}
            </Text>
            {ALL_FEATURES.map((feature) => <CheckBox
                title={localize(`org-form-feature-${feature}` as Parameters<typeof localize>[0])}
                isChecked={formData.features?.includes(feature) ? "checked" : undefined}
                onPress={() => handleToggleFeature(feature)}
                style={{
                    marginVertical: spaces.spacingXs
                }}
                spreadBehaviour="stretch"
                key={feature}
            />)}
        </ScrollView>
        <View
            style={[
                stylesheet.buttonsContainer,
                {
                    flexDirection: isMobile ? "column-reverse" : "row",
                    marginTop: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.buttonWrapper}
            >
                <Button
                    onPress={() => NCoreUIKitModal.close({
                        id: modalId
                    })}
                    title={localize("org-form-cancel")}
                    spreadBehaviour="stretch"
                    type="neutral"
                />
            </View>
            <View
                style={stylesheet.buttonWrapper}
            >
                <Button
                    title={isEdit ? localize("org-form-update") : localize("org-form-save")}
                    spreadBehaviour="stretch"
                    isLoading={isSubmitting}
                    onPress={handleSubmit}
                />
            </View>
        </View>
    </View>;
};
export default OrganizationFormModal;
